<script setup lang="ts">
import type {StyleValue} from 'vue';

interface ProjectFeatureProps {
  maxWidth?: string;
  textColor?: string;
  fontSize?: string;
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundRepeat?: string;
  backgroundSize?: string;
  leftBackgroundImage?: string;
  leftBackgroundColor?: string;
  rightBackgroundColor?: string;
  rightBackgroundImage?: string;
  leftBorderRight?: string;
  rightBorderLeft?: string;
  leftWidth?: string;
  rightWidth?: string;
  rightPosition?: 'absolute' | 'static';
  stackBreakpoint?: '1024px' | '767px';
  leftPanelClass?: string | string[];
  rightPanelClass?: string | string[];
  contentClass?: string | string[];
}

const props = withDefaults(defineProps<ProjectFeatureProps>(), {
  textColor: 'white',
  fontSize: '2.6rem',
  backgroundImage: '',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  leftBackgroundColor: 'transparent',
  rightBackgroundColor: 'transparent',
  rightBackgroundImage: '',
  leftBorderRight: 'none',
  rightBorderLeft: 'none',
  leftWidth: '50%',
  rightWidth: '50%',
  rightPosition: 'absolute',
  stackBreakpoint: '1024px',
  leftBackgroundImage: '',
  leftPanelClass: '',
  rightPanelClass: '',
  contentClass: '',
});

const featureStyle = computed<StyleValue>(() => ({
  ...(props.maxWidth ? {'--project-feature-max-width': props.maxWidth} : {}),
  '--project-feature-text-color': props.textColor,
  '--project-feature-font-size': props.fontSize,
  '--project-feature-background-image': props.backgroundImage ? `url("${props.backgroundImage}")` : 'none',
  '--project-feature-background-position': props.backgroundPosition,
  '--project-feature-background-repeat': props.backgroundRepeat,
  '--project-feature-background-size': props.backgroundSize,
  '--project-feature-left-background-image': props.leftBackgroundImage ? `url("${props.leftBackgroundImage}")` : 'none',
  '--project-feature-left-background-color': props.leftBackgroundColor,
  '--project-feature-right-background-color': props.rightBackgroundColor,
  '--project-feature-right-background-image': props.rightBackgroundImage ? `url("${props.rightBackgroundImage}")` : 'none',
  '--project-feature-left-border-right': props.leftBorderRight,
  '--project-feature-right-border-left': props.rightBorderLeft,
  '--project-feature-left-width': props.leftWidth,
  '--project-feature-right-width': props.rightWidth,
  '--project-feature-right-position': props.rightPosition,
}));

const stackClass = computed(() => `project-feature--stack-${props.stackBreakpoint.replace('px', '')}`);
</script>

<template>
  <div v-bind="$attrs" class="project-feature" :class="stackClass" :style="featureStyle">
    <div class="project-feature__content" :class="contentClass">
      <div class="project-feature__top">
        <div class="project-feature__left" :class="leftPanelClass">
          <slot name="left">
            <slot name="left-header" class="left-header" />
            <slot name="left-subtitle" />
            <slot name="left-body" />
            <slot name="left-footer" />
          </slot>
        </div>

        <div class="project-feature__right" :class="rightPanelClass">
          <slot name="right">
            <slot name="right-thumbnails" />
            <slot name="right-media" />
            <slot name="right-footer" />
          </slot>
        </div>
      </div>

      <slot name="bottom" />
    </div>
  </div>
</template>

<style lang="scss">
.project-feature {
  background-image: var(--project-feature-background-image);
  background-position: var(--project-feature-background-position);
  background-repeat: var(--project-feature-background-repeat);
  background-size: var(--project-feature-background-size);
  color: var(--project-feature-text-color);
  font-size: var(--project-feature-font-size);

  .feature-logo {
    @media (max-width: 767px) {
      h2 {
        font-size: 4.5rem !important;
      }
    }
  }

  &__content {
    display: block;
    margin: auto;
    max-width: var(--project-feature-max-width);
    position: relative;
    width: 100%;
  }

  &__top {
    display: flex;
    position: relative;
    width: 100%;

    @media (max-width: 1024px) {
      flex-direction: column;
    }
  }

  &__left,
  &__right {
    flex: 1;

    @media (max-width: 1024px) {
      flex: 1;
      width: auto;
    }
  }

  &__left {
    align-self: flex-start;
    background-color: var(--project-feature-left-background-color);
    background-image: var(--project-feature-left-background-image);
    background-position: center;
    background-size: cover;
    border-right: var(--project-feature-left-border-right);
    display: flex;
    flex: 0 0 var(--project-feature-left-width);
    flex-direction: column;
    padding: 5rem;
    position: relative;

    @media (max-width: 767px) {
      padding: 2rem 3rem;
    }
  }

  &__right {
    background-color: var(--project-feature-right-background-color);
    background-image: var(--project-feature-right-background-image);
    background-position: center;
    background-size: cover;
    border-left: var(--project-feature-right-border-left);
    display: flex;
    flex: 0 0 var(--project-feature-right-width);
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    position: var(--project-feature-right-position);
    right: 0;
    top: 0;
    width: var(--project-feature-right-width);

    @media (max-width: 1024px) {
      flex: 1;
      height: auto;
      overflow: visible;
      position: static;
      width: auto;
    }
  }

  &--stack-767 {
    @media (min-width: 768px) {
      .project-feature__top {
        flex-direction: row;
      }

      .project-feature__left {
        flex: 0 0 var(--project-feature-left-width);
        width: auto;
      }

      .project-feature__right {
        flex: 0 0 var(--project-feature-right-width);
        height: 100%;
        overflow: hidden;
        position: var(--project-feature-right-position);
        width: var(--project-feature-right-width);
      }
    }
  }
}
</style>
